import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";
import {
    Shield,
    Clock,
    CheckCircle2
} from "lucide-react";
import { Device } from "../interfaces/EndpointInterfaces";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../StorageHelper";
import { submitForm } from "./Api/tenantuserendpointsapi";
import "../ManagementDialog.css";
import "./TenantEndpoints.css";

interface TenantResourceRequestProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    resourceName: string;
    resourceType?: string;
    deviceName?: string;
    userId?: string;
    resourceId?: string;
    device: Device;
    user: any;
    publisher: string;
}

export default function TenantResourceRequest({
    open,
    onOpenChange,
    resourceName,
    resourceType = "Resource",
    deviceName = "",
    userId,
    user,
    resourceId,
    device,
    publisher
}: TenantResourceRequestProps) {
    const [accessType, setAccessType] = useState("DYNAMIC");
    const [justification, setJustification] = useState("");
    const [timeMinutes, setTimeMinutes] = useState("");
    let { email } = useUserStore((state) => state.user);
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

    // Extract filename from path for cleaner display
    const getDisplayName = (path: string) => {
        if (!path) return "Resource";

        // For file paths, extract just the filename
        if (path.includes("\\") || path.includes("/")) {
            const parts = path.split(/[\\/]/);
            return parts[parts.length - 1] || path;
        }

        return path;
    };

    const displayName = getDisplayName(resourceName);


    const isDeviceSSh = (device: Device) => {
        return (
            device?.OperatingSystem?.toLowerCase().includes("linux") ||
            device?.OperatingSystem?.toLowerCase().includes("macos")
        );
    };

    const handleSubmit = async () => {


        const isServer = device?.belongedToUser
            ? device.belongedToUser === email
                ? false
                : device?.isServer
            : device?.isServer;

        const payload = {
            userId: userId,
            username: user.cn,
            email:
                isDeviceSSh(device)
                    ? email
                    : user?.email,
            resourceType: resourceType,
            resourceId: resourceId,
            resourceName,
            computerName:
                isDeviceSSh(device)
                    ? user?.email
                    : device?.dNSHostName,
            friendlyName: device?.friendlyName,
            resourcePath: resourceName || "",
            requestMessage: justification,
            requestStatus: "PENDING",
            selectedTime: accessType.toUpperCase() === "PERMANENT" ? 0 : timeMinutes,
            publisherId: resourceType === "APP" ? publisher : null,
            computerId: device?.id,
            createdAt: new Date().toISOString(),
            privilegeLevel: accessType.toUpperCase(),
            tenantName,
            userEmail: email,
            toConnect: false,
            isServer: isServer,
            machineUser: user.cn,
            machineUserId: userId,
            connectionMode: null,
            connectionUser: null,
            connectionLabel: null,
            type: "onPrem",
            timingMode: accessType.toUpperCase() === "PERMANENT" ? "PERMANENT" : "GENERIC",
            scheduledStart: null,
            scheduledEnd: null,
        };



        await submitForm(payload);
        handleCancel();
    };

    const handleCancel = () => {
        onOpenChange(false);
        // Reset form
        setAccessType("DYNAMIC");
        setJustification("");
        setTimeMinutes("");
    };

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setAccessType("timed");
            setJustification("");
            setTimeMinutes("");
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="management-dialog tenant-access-dialog max-w-md">
                <DialogHeader className="management-dialog-header">
                    <div className="management-dialog-heading">
                        <span className="management-dialog-icon">
                            <Shield aria-hidden="true" />
                        </span>
                        <div>
                            <DialogTitle>Request Access</DialogTitle>
                            <p className="text-muted-foreground">
                                Request access to <span className="font-medium text-foreground">{displayName}</span>
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                <div className="management-dialog-body">
                    <section className="management-form-section">
                        <div className="management-section-title">
                            <Clock aria-hidden="true" />
                            Access Type
                        </div>
                        <RadioGroup value={accessType} onValueChange={setAccessType} className="tenant-access-options">
                            <div className="relative">
                                <RadioGroupItem value="DYNAMIC" id="DYNAMIC" className="peer sr-only" />
                                <Label htmlFor="DYNAMIC" className="tenant-access-option">
                                    <Clock aria-hidden="true" data-tone="warning" />
                                    <span>Timed</span>
                                </Label>
                            </div>
                            <div className="relative">
                                <RadioGroupItem value="PERMANENT" id="PERMANENT" className="peer sr-only" />
                                <Label htmlFor="PERMANENT" className="tenant-access-option">
                                    <CheckCircle2 aria-hidden="true" data-tone="success" />
                                    <span>Permanent</span>
                                </Label>
                            </div>
                        </RadioGroup>

                        <div className="tenant-access-summary">
                            <div data-wide="true">
                                <span>{resourceType}</span>
                                <strong className="font-mono">{resourceName}</strong>
                            </div>
                            {deviceName && (
                                <div data-wide="true">
                                    <span>Computer</span>
                                    <strong>{deviceName}</strong>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="management-form-section">
                        <div className="management-form-field">
                            <Label htmlFor="justification">Access Justification *</Label>
                            <Textarea
                                id="justification"
                                value={justification}
                                onChange={(e) => setJustification(e.target.value)}
                                placeholder="Provide business justification for this access request..."
                                data-testid="textarea-justification"
                            />
                        </div>

                        {accessType === "DYNAMIC" && (
                            <div className="management-form-field">
                                <Label htmlFor="time-minutes">Duration (minutes) *</Label>
                                <Input
                                    id="time-minutes"
                                    value={timeMinutes}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === "") {
                                            setTimeMinutes("");
                                            return;
                                        }
                                        if (!/^\d+$/.test(value)) return;
                                        const num = Number(value);
                                        if (num >= 1 && num <= 999) {
                                            setTimeMinutes(value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    placeholder="e.g., 60"
                                    type="number"
                                    min="1"
                                    max="999"
                                    className="tenant-access-duration"
                                    data-testid="input-time-minutes"
                                />
                            </div>
                        )}
                    </section>
                </div>

                <div className="management-dialog-footer">
                    <Button variant="outline" onClick={handleCancel} data-testid="button-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={
                            !justification.trim() ||
                            (accessType === "DYNAMIC" && !timeMinutes)
                        }
                        data-testid="button-submit"
                        className="endpoint-primary-action"
                    >
                        <Shield className="w-4 h-4 mr-1" />
                        Submit Request
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}